import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

const DRAG_THRESHOLD = 4;

/**
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper
 * @property {HTMLElement} content
 * @property {HTMLElement[]} marqueeItems
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  async connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    this.#syncLayout();
    this.#lastInlineWidth = this.#getInlineWidth();
    this.#resizeObserver.observe(this);

    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);

    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointermove', this.#onPointerMove);
    this.addEventListener('pointerup', this.#onPointerUp);
    this.addEventListener('pointercancel', this.#onPointerUp);
    this.addEventListener('click', this.#onClickCapture, true);
    this.addEventListener('dragstart', this.#preventDefault);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);

    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('pointermove', this.#onPointerMove);
    this.removeEventListener('pointerup', this.#onPointerUp);
    this.removeEventListener('pointercancel', this.#onPointerUp);
    this.removeEventListener('click', this.#onClickCapture, true);
    this.removeEventListener('dragstart', this.#preventDefault);
  }

  /** @type {{ cancel: () => void, current: number } | null} */
  #animation = null;

  /** @type {{ pointerId: number, startX: number, startTime: number, duration: number, totalDistance: number, moved: boolean, sign: number } | null} */
  #drag = null;

  /**
   * Element inline width used for the last layout sync. iOS Safari expands/collapses browser chrome
   * while scrolling and can emit viewport resize events without a real marquee width change; observing
   * the element width avoids rebuilding/restarting the animation for those height-only changes.
   * @type {number}
   */
  #lastInlineWidth = 0;

  #resizeObserver = new ResizeObserver(
    debounce(() => {
      const inlineWidth = this.#getInlineWidth();
      if (inlineWidth === 0 || inlineWidth === this.#lastInlineWidth) return;

      this.#lastInlineWidth = inlineWidth;
      this.#syncLayout({ preserveProgress: true });
    }, 150)
  );

  #preventDefault = (event) => event.preventDefault();

  #slowDown = debounce((event) => {
    if (event.pointerType !== 'mouse') return;
    if (this.#animation || this.#drag) return;

    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp = () => {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  };

  #getMotionAnimation() {
    return this.refs.wrapper.getAnimations()[0] ?? null;
  }

  #getInlineWidth() {
    return Math.round(this.getBoundingClientRect().width);
  }

  #onPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const anim = this.#getMotionAnimation();
    if (!anim) return;

    this.#slowDown.cancel();
    this.#animation?.cancel();
    this.#animation = null;
    anim.updatePlaybackRate(1);
    anim.pause();

    const duration = Number(anim.effect.getComputedTiming().duration) || 0;
    if (!duration) {
      anim.play();
      return;
    }

    const gap = parseFloat(getComputedStyle(this).getPropertyValue('--marquee-gap')) || 0;
    const totalDistance = this.refs.wrapper.offsetWidth / 2 + gap / 2;

    this.#drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startTime: Number(anim.currentTime) || 0,
      duration,
      totalDistance,
      moved: false,
      sign: this.dataset.movementDirection === 'reverse' ? 1 : -1,
    };

    try {
      this.setPointerCapture(event.pointerId);
    } catch (_) {
      /* noop */
    }
  };

  #onPointerMove = (event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    const deltaX = event.clientX - drag.startX;
    if (!drag.moved) {
      if (Math.abs(deltaX) <= DRAG_THRESHOLD) return;
      drag.moved = true;
      this.setAttribute('data-dragging', '');
    }

    const anim = this.#getMotionAnimation();
    if (!anim) return;

    const offset = drag.sign * (deltaX / drag.totalDistance) * drag.duration;
    let newTime = drag.startTime + offset;
    newTime = ((newTime % drag.duration) + drag.duration) % drag.duration;
    anim.currentTime = newTime;
  };

  #onPointerUp = (event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    try {
      this.releasePointerCapture(drag.pointerId);
    } catch (_) {
      /* noop */
    }

    const anim = this.#getMotionAnimation();
    if (anim) anim.play();

    const wasMoving = drag.moved;
    this.#drag = null;

    if (wasMoving) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.removeAttribute('data-dragging'));
      });
    } else {
      this.removeAttribute('data-dragging');
    }
  };

  #onClickCapture = (event) => {
    if (this.hasAttribute('data-dragging')) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;
    return content !== lastChild ? lastChild : null;
  }

  /** @param {number} value */
  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  /** @param {number} value */
  #setDistance(value) {
    this.style.setProperty('--marquee-distance', `${-value}px`);
  }

  /** @param {number} numberOfCopies */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    return Math.sqrt(numberOfCopies) * speedFactor;
  }

  #measureLayout() {
    const firstItem = this.refs.marqueeItems[0];
    if (!firstItem) return null;

    const gap = parseFloat(getComputedStyle(this).getPropertyValue('--marquee-gap')) || 0;
    const containerWidth = this.getBoundingClientRect().width;
    const itemWidth = firstItem.getBoundingClientRect().width;
    const numberOfCopies = itemWidth === 0 ? 1 : Math.max(1, Math.ceil(containerWidth / itemWidth));
    const distance = numberOfCopies * (itemWidth + gap);

    return { numberOfCopies, distance };
  }

  #getAnimationProgress() {
    const animation = this.#getMotionAnimation();
    const duration = Number(animation?.effect?.getComputedTiming().duration) || 0;
    if (!animation || !duration) return null;

    const currentTime = Number(animation.currentTime) || 0;
    return ((currentTime % duration) + duration) % duration / duration;
  }

  /** @param {number} progress */
  #restoreAnimationProgress(progress) {
    requestAnimationFrame(() => {
      const animation = this.#getMotionAnimation();
      const duration = Number(animation?.effect?.getComputedTiming().duration) || 0;
      if (!animation || !duration) return;

      animation.currentTime = progress * duration;
    });
  }

  /** @param {{ preserveProgress?: boolean }} [options] */
  #syncLayout(options = {}) {
    const layout = this.#measureLayout();
    if (!layout) return;

    const progress = options.preserveProgress ? this.#getAnimationProgress() : null;

    this.#setNumberOfCopies(layout.numberOfCopies);
    this.#duplicateContent();
    this.#setDistance(layout.distance);
    this.#setSpeed(this.#calculateSpeed(layout.numberOfCopies));

    if (progress !== null) {
      this.#restoreAnimationProgress(progress);
    }
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');
    clone.querySelectorAll('[ref]').forEach((element) => element.removeAttribute('ref'));

    this.refs.wrapper.appendChild(clone);
  }

  /** @param {number} numberOfCopies */
  #setNumberOfCopies(numberOfCopies) {
    const { content, marqueeItems } = this.refs;
    if (!marqueeItems[0]) return;

    const currentNumberOfCopies = content.children.length;
    if (numberOfCopies === currentNumberOfCopies) return;

    if (numberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - numberOfCopies);
      return;
    }

    for (let i = 0; i < numberOfCopies - currentNumberOfCopies; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  /** @param {number} numberOfCopies */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children = Array.from(content.children);

    const itemsToRemove = Math.min(numberOfCopies, children.length - 1);
    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }
}

/**
 * @param {Object} params
 * @param {number} params.from
 * @param {number} params.to
 * @param {number} params.duration
 * @param {function(number): void} params.onUpdate
 * @param {function(number): number} [params.easing]
 * @param {function(): void} [params.onComplete]
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
